import { useCallback, useEffect, useRef } from "react"
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, type CarouselApi } from "../components/ui/carousel"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer, IteratedElement } from "./element-renderer"
import { cn } from "../lib/utils"

export function CarouselRenderer({ element }: RendererProps) {
  const ctx = useRenderContext()
  const { dispatchEvent, resolveSymbol } = ctx
  const apiRef = useRef<CarouselApi | undefined>(undefined)
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: `element.variant === "auto"` is a legacy autoplay-behavior flag
  // used by the autoplay useEffect below; data-variant carries the raw
  // token, which overlaps with that behavior path harmlessly.
  const atomDefaults = useAtomDefaults("carousel")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // Support iterateOver: expand single child template into N slides
  let slides = element.children ?? []
  let iterItems: unknown[] | null = null
  const itemVar = element.itemVar
  if (element.iterateOver && slides.length === 1) {
    const items = resolveSymbol(element.iterateOver)
    if (Array.isArray(items) && items.length > 0) {
      iterItems = items
      const template = slides[0]
      slides = items.map((_item, i) => ({
        ...template,
        id: `${template.id}__slide_${i}`,
      }))
    }
  }

  const slideCount = slides.length

  // Dispatch slideChange event when embla selects a new slide
  const handleSetApi = useCallback(
    (api: CarouselApi) => {
      apiRef.current = api
      if (!api) return
      api.on("select", () => {
        dispatchEvent(element.id, "slideChange", { index: api.selectedScrollSnap() })
      })
    },
    [element.id, dispatchEvent],
  )

  // Auto-advance when variant is "auto"
  useEffect(() => {
    const api = apiRef.current
    if (element.variant === "auto" && slideCount > 1 && api) {
      const interval = setInterval(() => {
        const next = ((api.selectedScrollSnap?.() ?? 0) + 1) % slideCount
        api.scrollTo(next)
      }, 4000)
      return () => clearInterval(interval)
    }
  }, [element.variant, slideCount])

  if (slideCount === 0) {
    return (
      <div
        className={cn(
          "flex h-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground",
          element.className,
        )}
        data-testid={`carousel-${element.id}`}
        data-atom-type="carousel"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
      >
        No slides
      </div>
    )
  }

  return (
    <Carousel
      className={cn("w-full", element.className)}
      data-testid={`carousel-${element.id}`}
      data-atom-type="carousel"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      setApi={handleSetApi}
      opts={{ loop: true }}
    >
      <CarouselContent>
        {slides.map((slide, i) => (
          <CarouselItem key={slide.id}>
            {iterItems && itemVar ? (
              <IteratedElement
                element={slide}
                item={iterItems[i]}
                itemVar={itemVar}
                parentCtx={ctx}
                childIndex={i}
              />
            ) : (
              <ElementRenderer element={slide} />
            )}
          </CarouselItem>
        ))}
      </CarouselContent>
      {slideCount > 1 && (
        <>
          <CarouselPrevious />
          <CarouselNext />
        </>
      )}
    </Carousel>
  )
}
